import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { useAuth } from "../auth/AuthContext";
import {
  canControlCounterparties,
  canMutateCounterparties,
  createCounterparty,
  deleteCounterparty,
  fetchCounterparties,
  suggestByInn,
  updateCounterparty,
  type Counterparty,
  type CounterpartyInput,
  type PartySuggestion,
} from "../lib/counterparties";

/**
 * Экран «Контрагенты» (E3, справочник). Просмотр — всем ролям; создание/правка/удаление —
 * ролям регистрации договоров (`contract:register`): сервер проверяет право на каждом маршруте
 * (матрица 4.3), UI лишь прячет кнопки (правило 5). Оформление — токены docs/07 (правило 9):
 * ИНН/КПП/ОГРН моноширинным (PT Mono), штамп аффилированности.
 *
 * ИНН проверяется по контрольному числу на сервере (E3); ошибку показываем как есть.
 * Автозаполнение по ИНН (DaData, E3a): кнопка «Заполнить по ИНН» тянет реквизиты с сервера
 * (наружу уходит только ИНН). При выключенной/недоступной DaData подсказок просто нет — форма
 * остаётся обычным ручным вводом (мягкая деградация, без ошибок).
 */

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly items: Counterparty[] }
  | { readonly kind: "error"; readonly message: string };

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";

/**
 * Полный ли ИНН для запроса подсказок (10 или 12 цифр). Контрольное число проверяет сервер;
 * здесь — лишь когда включать кнопку «Заполнить по ИНН» и стоит ли вообще дёргать DaData.
 */
function isCompleteInn(value: string): boolean {
  const inn = value.trim();
  return /^\d{10}$/.test(inn) || /^\d{12}$/.test(inn);
}

/**
 * Штамп аффилированности: внутригрупповой контрагент выделен акцентом (ТЗ 3.1, 4.2.1).
 * Признак справочный: режим контроля сроков общий, эскалации и претензионные сигналы по таким
 * договорам идут как для всех — приглушить их можно только настройкой инстанса, выключенной
 * по умолчанию (В18, Р-35).
 */
function AffiliatedStamp({ affiliated }: { affiliated: boolean }) {
  return affiliated ? (
    <span
      className="stamp text-accent bg-control-bg"
      title="Внутригрупповой контрагент: контроль сроков общий, эскалации идут как для всех"
    >
      Аффилирован
    </span>
  ) : (
    <span className="stamp text-control-fg bg-control-bg">Внешний</span>
  );
}

export function CounterpartiesPage() {
  const { user, logout } = useAuth();
  const canMutate = canMutateCounterparties(user?.roles ?? []);
  // Контроль-влияющие операции (аффилированность, удаление) — только юрист/админ (решение E3).
  const canControl = canControlCounterparties(user?.roles ?? []);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Контрагент в правке (null — режим создания). Форма показывается только мутаторам.
  const [editing, setEditing] = useState<Counterparty | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchCounterparties();
    if (res.ok) {
      setState({ kind: "ready", items: res.value });
      return;
    }
    if (res.reason === "unauthorized") {
      await logout();
      return;
    }
    setState({ kind: "error", message: res.message });
  }, [logout]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (cp: Counterparty) => {
    setEditing(cp);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const onSaved = useCallback(() => {
    closeForm();
    void load();
  }, [load]);

  return (
    <section aria-labelledby="cp-title">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 id="cp-title" className="text-[20px] font-bold text-ink">
          Контрагенты
        </h1>
        {canMutate && !formOpen && (
          <button type="button" onClick={openCreate} className={btnPrimary}>
            Новый контрагент
          </button>
        )}
      </div>
      <p className="mt-1 text-[13px] text-muted">
        Справочник контрагентов: реквизиты, ИНН (с проверкой контрольного числа) и признак
        аффилированности. Реквизиты можно подставить по ИНН из DaData — кнопкой «Заполнить по ИНН».
      </p>

      {canMutate && formOpen && (
        <div className="mt-4">
          <CounterpartyForm
            editing={editing}
            canControl={canControl}
            onSaved={onSaved}
            onCancel={closeForm}
          />
        </div>
      )}

      <div className="mt-6">
        {state.kind === "loading" && (
          <p className="text-muted" role="status">
            Загрузка справочника…
          </p>
        )}

        {state.kind === "error" && (
          <div
            role="alert"
            className="rounded-[var(--radius-l)] border border-border bg-surface p-5"
          >
            <p className="text-overdue-fg">{state.message}</p>
            <button type="button" onClick={() => void load()} className={`mt-3 ${btnPrimary}`}>
              Повторить
            </button>
          </div>
        )}

        {state.kind === "ready" && (
          <CounterpartiesTable
            items={state.items}
            canMutate={canMutate}
            canControl={canControl}
            onEdit={openEdit}
            onChanged={load}
          />
        )}
      </div>
    </section>
  );
}

/** Форма создания/правки контрагента (общая; режим определяется наличием `editing`). */
function CounterpartyForm({
  editing,
  canControl,
  onSaved,
  onCancel,
}: {
  editing: Counterparty | null;
  canControl: boolean;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? "");
  const [inn, setInn] = useState(editing?.inn ?? "");
  const [kpp, setKpp] = useState(editing?.kpp ?? "");
  const [ogrn, setOgrn] = useState(editing?.ogrn ?? "");
  const [address, setAddress] = useState(editing?.address ?? "");
  const [affiliated, setAffiliated] = useState(editing?.affiliated ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Автозаполнение по ИНН (E3a). `suggestions` — список выбора (голова + филиалы), когда DaData
  // вернула больше одного совпадения; `suggestNote` — нейтральная подсказка («ничего не нашлось»),
  // это НЕ ошибка формы. При правке ИНН всё сбрасываем, чтобы не осталось устаревших вариантов.
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestions, setSuggestions] = useState<PartySuggestion[]>([]);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  // «Поколение» запроса подсказок: любое изменение ИНН или новый поиск инкрементят счётчик,
  // и запоздавший ответ прежнего запроса не применяется (иначе форма заполнится по старому ИНН).
  const lookupSeq = useRef(0);

  const title = editing ? "Изменение контрагента" : "Новый контрагент";

  const onInnChange = (value: string) => {
    lookupSeq.current += 1; // правка ИНН обесценивает ответ поиска, идущего в этот момент
    setInn(value);
    setSuggestions([]);
    setSuggestNote(null);
    setSuggestBusy(false);
  };

  /** Заполняет реквизиты выбранной подсказкой. ИНН оставляем как ввёл пользователь. */
  const applySuggestion = (s: PartySuggestion) => {
    setName(s.name);
    setKpp(s.kpp ?? "");
    setOgrn(s.ogrn ?? "");
    setAddress(s.address ?? "");
    setSuggestions([]);
    setSuggestNote(null);
  };

  const onLookupInn = async () => {
    const seq = (lookupSeq.current += 1);
    setSuggestNote(null);
    setSuggestions([]);
    setSuggestBusy(true);
    const found = await suggestByInn(inn.trim());
    // Ответ устарел (пользователь изменил ИНН или запустил новый поиск) — не применяем.
    if (lookupSeq.current !== seq) return;
    setSuggestBusy(false);
    if (found.length === 1) {
      applySuggestion(found[0]!);
      return;
    }
    if (found.length > 1) {
      setSuggestions(found);
      return;
    }
    // Пусто — не ошибка: DaData могла быть выключена/недоступна или ИНН не найден.
    setSuggestNote("По этому ИНН ничего не нашлось — заполните реквизиты вручную.");
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const input: CounterpartyInput = {
      name: name.trim(),
      inn: inn.trim(),
      kpp: kpp.trim(),
      ogrn: ogrn.trim(),
      address: address.trim(),
      affiliated,
    };
    const res = editing
      ? await updateCounterparty(editing.id, input)
      : await createCounterparty(input);
    setBusy(false);
    if (res.ok) {
      onSaved();
      return;
    }
    setError(res.message);
  };

  return (
    <form
      onSubmit={onSubmit}
      aria-labelledby="cp-form-title"
      className="rounded-[var(--radius-l)] border border-border bg-surface p-4"
    >
      <h2 id="cp-form-title" className="mb-3 text-[15px] font-bold text-ink">
        {title}
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="cp-name" className="mb-1 block text-[13px] text-muted">
            Наименование
          </label>
          <input
            id="cp-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
            required
          />
        </div>
        <div>
          <label htmlFor="cp-inn" className="mb-1 block text-[13px] text-muted">
            ИНН
          </label>
          <div className="flex items-center gap-2">
            <input
              id="cp-inn"
              value={inn}
              onChange={(e) => onInnChange(e.target.value)}
              inputMode="numeric"
              autoComplete="off"
              placeholder="10 или 12 цифр"
              className={`num ${inputCls}`}
            />
            {/* Автозаполнение по ИНН (E3a): активно при полном ИНН. Наружу уходит только ИНН. */}
            <button
              type="button"
              onClick={() => void onLookupInn()}
              disabled={suggestBusy || !isCompleteInn(inn)}
              className={`${btnSecondary} whitespace-nowrap`}
              title="Заполнить реквизиты по ИНН из DaData"
            >
              {suggestBusy ? "Поиск…" : "Заполнить по ИНН"}
            </button>
          </div>
          {suggestNote && (
            <p role="status" className="mt-1 text-[12px] text-muted">
              {suggestNote}
            </p>
          )}
          {suggestions.length > 0 && (
            <ul className="mt-2 space-y-1.5" aria-label="Найденные организации по ИНН">
              {suggestions.map((s, i) => (
                <li
                  key={`${s.inn}-${s.kpp ?? i}`}
                  className="flex items-start justify-between gap-2 rounded-[var(--radius-m)] border border-border bg-bg px-2.5 py-1.5"
                >
                  <span className="text-[13px] text-ink">
                    {s.name}
                    {s.kpp && <span className="num text-muted"> · КПП {s.kpp}</span>}
                    {s.address && <span className="block text-[12px] text-muted">{s.address}</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => applySuggestion(s)}
                    className={`${btnSecondary} whitespace-nowrap`}
                  >
                    Выбрать
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label htmlFor="cp-kpp" className="mb-1 block text-[13px] text-muted">
            КПП
          </label>
          <input
            id="cp-kpp"
            value={kpp}
            onChange={(e) => setKpp(e.target.value)}
            inputMode="numeric"
            autoComplete="off"
            className={`num ${inputCls}`}
          />
        </div>
        <div>
          <label htmlFor="cp-ogrn" className="mb-1 block text-[13px] text-muted">
            ОГРН
          </label>
          <input
            id="cp-ogrn"
            value={ogrn}
            onChange={(e) => setOgrn(e.target.value)}
            inputMode="numeric"
            autoComplete="off"
            className={`num ${inputCls}`}
          />
        </div>
        <div>
          <label htmlFor="cp-address" className="mb-1 block text-[13px] text-muted">
            Адрес
          </label>
          <input
            id="cp-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* Признак аффилированности меняет только юрист/админ: рядовому регистратору
          показываем текущее значение, но чекбокс отключён (сервер тоже вернёт 403). */}
      <label className="mt-3 inline-flex items-center gap-2 text-[13px] text-ink">
        <input
          type="checkbox"
          checked={affiliated}
          onChange={(e) => setAffiliated(e.target.checked)}
          disabled={!canControl}
          className="accent-accent disabled:opacity-50"
        />
        Аффилированный (внутригрупповой) — контроль сроков общий
      </label>
      <p className="mt-1 text-[12px] text-muted">
        Эскалации и претензионные сигналы по таким контрагентам идут как для всех. Приглушить их
        можно только настройкой системы, которая по умолчанию выключена (её задаёт администратор).
        {!canControl && " Признак аффилированности устанавливает юрист или администратор."}
      </p>

      {error && (
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>
          {busy ? "Сохранение…" : editing ? "Сохранить" : "Создать"}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className={btnSecondary}>
          Отмена
        </button>
      </div>
    </form>
  );
}

function CounterpartiesTable({
  items,
  canMutate,
  canControl,
  onEdit,
  onChanged,
}: {
  items: Counterparty[];
  canMutate: boolean;
  canControl: boolean;
  onEdit: (cp: Counterparty) => void;
  onChanged: () => Promise<void>;
}) {
  if (items.length === 0) {
    return <p className="text-muted">Контрагентов пока нет.</p>;
  }
  return (
    <div className="min-w-fit rounded-[var(--radius-l)] border border-border bg-surface">
      <table className="sticky-head w-full border-collapse text-[14px]">
        <caption className="sr-only">Справочник контрагентов</caption>
        <thead>
          <tr className="border-b border-border text-left text-[12px] text-muted">
            <th scope="col" className="px-4 py-2.5 font-bold">
              Наименование
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Адрес
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              ИНН
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              КПП
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Признак
            </th>
            <th scope="col" className="px-4 py-2.5 font-bold">
              Договоров
            </th>
            {canMutate && (
              <th scope="col" className="px-4 py-2.5 font-bold">
                Действия
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((cp) => (
            <CounterpartyRow
              key={cp.id}
              cp={cp}
              canMutate={canMutate}
              canControl={canControl}
              onEdit={onEdit}
              onChanged={onChanged}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CounterpartyRow({
  cp,
  canMutate,
  canControl,
  onEdit,
  onChanged,
}: {
  cp: Counterparty;
  canMutate: boolean;
  canControl: boolean;
  onEdit: (cp: Counterparty) => void;
  onChanged: () => Promise<void>;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deletable = cp.contractCount === 0;

  const doDelete = async () => {
    setBusy(true);
    setError(null);
    const res = await deleteCounterparty(cp.id);
    setBusy(false);
    setConfirmDelete(false);
    if (res.ok) {
      await onChanged();
      return;
    }
    setError(res.message);
  };

  return (
    <tr className="border-b border-border-soft align-top last:border-b-0">
      <td className="px-4 py-3 text-ink">{cp.name}</td>
      <td className="px-4 py-3 text-[13px] text-muted">{cp.address ?? "—"}</td>
      <td className="num px-4 py-3 whitespace-nowrap text-ink">{cp.inn ?? "—"}</td>
      <td className="num px-4 py-3 whitespace-nowrap text-muted">{cp.kpp ?? "—"}</td>
      <td className="px-4 py-3 whitespace-nowrap">
        <AffiliatedStamp affiliated={cp.affiliated} />
      </td>
      <td className="num px-4 py-3 whitespace-nowrap text-ink">{cp.contractCount}</td>
      {canMutate && (
        <td className="px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {confirmDelete ? (
              <>
                <span className="text-[13px] text-ink">Удалить контрагента?</span>
                <button
                  type="button"
                  disabled={busy}
                  className={btnPrimary}
                  onClick={() => void doDelete()}
                >
                  Подтвердить
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className={btnSecondary}
                  onClick={() => setConfirmDelete(false)}
                >
                  Отмена
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={busy}
                  className={btnSecondary}
                  onClick={() => onEdit(cp)}
                >
                  Изменить
                </button>
                {/* Удаление — контроль-влияющая операция (только юрист/админ, решение E3). */}
                {canControl && (
                  <button
                    type="button"
                    disabled={busy || !deletable}
                    className={btnSecondary}
                    title={deletable ? undefined : "Нельзя удалить: по контрагенту есть договоры"}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Удалить
                  </button>
                )}
              </>
            )}
          </div>
          {error && (
            <p role="alert" className="mt-1.5 text-[12px] text-overdue-fg">
              {error}
            </p>
          )}
        </td>
      )}
    </tr>
  );
}
